import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, ChevronDown, Home, Building2 } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { useLanguage } from '../context/languagecontext';
import { useAudience } from '../context/audiencecontext';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇨🇦' },
  { code: 'uk', label: 'Українська', flag: '🇺🇦' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'tl', label: 'Filipino', flag: '🇵🇭' },
];

const Navigation = () => {
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { t, language, changeLanguage } = useLanguage();
  const { audienceType, isHomeowner, isCommercial, setShowSelector } = useAudience();
  const location = useLocation();

  const currentLang = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/services', label: t('nav.services') },
    { to: '/gallery', label: t('nav.gallery') },
    { to: '/about', label: t('nav.about') },
    { to: '/blog', label: 'Blog' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleLangSelect = (code) => {
    changeLanguage(code);
    setLangOpen(false);
  };

  return (
    <nav className="fixed w-full z-50 bg-white border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 h-20 flex items-center justify-between">

        <Link to="/" onClick={() => setOpen(false)}>
          <span className="font-heading text-2xl font-black tracking-tighter text-slate-900">
            SOUTH <span className="text-orange-600">PALMYRA</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={isActive(link.to)
                ? 'font-bold uppercase text-sm tracking-widest text-orange-600'
                : 'font-bold uppercase text-sm tracking-widest text-slate-600 hover:text-orange-600 transition-colors'}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">

          <button
            onClick={() => setShowSelector(true)}
            className="hidden md:flex items-center gap-2 px-3 py-2 rounded-sm border border-slate-200 hover:border-orange-500 transition-colors text-slate-700 hover:text-orange-600"
            title="Switch audience"
          >
            {isCommercial
              ? <Building2 className="w-4 h-4 text-orange-500" />
              : isHomeowner
              ? <Home className="w-4 h-4 text-orange-500" />
              : <Home className="w-4 h-4 text-slate-400" />
            }
            <span className="text-xs font-bold uppercase tracking-wider">
              {isCommercial ? 'Commercial' : isHomeowner ? 'Homeowner' : 'Who are you?'}
            </span>
          </button>

          <div className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-2 px-3 py-2 rounded-sm border border-slate-200 hover:border-orange-500 transition-colors text-slate-700 hover:text-orange-600"
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-bold uppercase tracking-wider hidden sm:block">
                {currentLang.flag} {currentLang.code.toUpperCase()}
              </span>
              <span className="text-sm font-bold uppercase tracking-wider sm:hidden">
                {currentLang.flag}
              </span>
              <ChevronDown className={`w-3 h-3 transition-transform ${langOpen ? 'rotate-180' : ''}`} />
            </button>

            {langOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setLangOpen(false)} />
                <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-sm shadow-lg z-20 py-1 min-w-[160px]">
                  <div className="px-3 py-2 border-b border-gray-100">
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-400">
                      Select Language
                    </span>
                  </div>
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLangSelect(lang.code)}
                      className={`w-full text-left px-4 py-2 text-sm font-body flex items-center gap-3 hover:bg-orange-50 hover:text-orange-600 transition-colors ${
                        language === lang.code
                          ? 'text-orange-600 bg-orange-50 font-bold'
                          : 'text-slate-700'
                      }`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      <span>{lang.label}</span>
                      {language === lang.code && (
                        <span className="ml-auto text-orange-500">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <Link
            to="/contact"
            className="hidden sm:block bg-orange-600 text-white px-5 py-2 font-bold uppercase text-xs tracking-wider hover:bg-slate-900 transition-all rounded-sm"
          >
            Get Quote
          </Link>

          <button
            onClick={() => setOpen(!open)}
            className="md:hidden p-2 text-slate-700 hover:text-orange-600 transition-colors"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

        </div>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                className={isActive(link.to)
                  ? 'block px-4 py-3 font-bold uppercase text-sm tracking-wider rounded-sm text-orange-600 bg-orange-50'
                  : 'block px-4 py-3 font-bold uppercase text-sm tracking-wider rounded-sm text-slate-700 hover:text-orange-600 hover:bg-slate-50'}
              >
                {link.label}
              </Link>
            ))}

            <button
              onClick={() => { setShowSelector(true); setOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 font-bold uppercase text-sm tracking-wider rounded-sm text-slate-700 hover:text-orange-600 hover:bg-slate-50"
            >
              {isCommercial
                ? <Building2 className="w-4 h-4 text-orange-500" />
                : <Home className="w-4 h-4 text-slate-400" />
              }
              {isCommercial ? 'Switch to Homeowner' : isHomeowner ? 'Switch to Commercial' : 'Who are you?'}
            </button>

            <div className="pt-3 border-t border-gray-100">
              <p className="px-4 pb-2 text-xs font-bold uppercase tracking-wider text-gray-400">
                Select Language
              </p>
              <div className="grid grid-cols-2 gap-2 px-2">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => { handleLangSelect(lang.code); setOpen(false); }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-bold transition-colors ${
                      language === lang.code
                        ? 'bg-orange-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-orange-50 hover:text-orange-600'
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-2 px-2">
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="block text-center bg-orange-600 text-white px-5 py-3 font-bold uppercase text-xs tracking-wider hover:bg-slate-900 transition-all rounded-sm"
              >
                Get Quote
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
